import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const field = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

/**
 * Teach the lead assistant a real patient question and the answer to give.
 * The question (plus any other wordings of it) is saved as examples under a
 * knowledge-base topic, new or existing; from then on the assistant answers
 * anything that closely matches. Used in the clinic's Lead Inbox (when the
 * clinic may teach) and in Platform Admin — each passes its own API calls.
 *
 * loadTopics(): Promise<[{ topic }]>; teach(payload): Promise<{ message }>
 */
export default function TeachAnswerDialog({
  open,
  onOpenChange,
  question: initialQuestion,
  answer: initialAnswer,
  extraQuestions = [],
  messageIds = [],
  loadTopics,
  teach,
  onDone,
}) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [target, setTarget] = useState("new");
  const [topic, setTopic] = useState("");
  const [topics, setTopics] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuestion(initialQuestion || "");
    setAnswer(initialAnswer || "");
    setTarget("new");
    setTopic("");
    loadTopics().then((list) => setTopics(list || [])).catch(() => setTopics([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuestion, initialAnswer]);

  const save = async () => {
    if (!question.trim() || !answer.trim()) return toast.error("The question and the answer are both needed.");
    setBusy(true);
    try {
      const res = await teach({
        question: question.trim(),
        extra_questions: extraQuestions.filter((q) => q.trim() && q.trim() !== question.trim()),
        answer: answer.trim(),
        topic: target === "new" ? topic.trim() : "",
        kb_index: target === "new" ? null : Number(target),
        message_ids: messageIds,
      });
      toast.success(res?.message || "Taught.");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Teach the assistant</DialogTitle>
          <DialogDescription>
            Next time a patient asks something like this, the assistant replies with this answer by itself. Check it's right for
            every patient, not just one.
          </DialogDescription>
        </DialogHeader>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Patient's question</span>
          <Input value={question} onChange={(e) => setQuestion(e.target.value)} className="mt-1" />
        </label>
        {extraQuestions.length > 1 && (
          <p className="text-xs text-muted-foreground">
            Also learns: {extraQuestions.filter((q) => q !== question).map((q) => `“${q}”`).join(", ")}
          </p>
        )}
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Answer to give</span>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={4}
            placeholder="As you'd tell the patient"
            className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Save under</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} className={`${field} mt-1`}>
            <option value="new">A new topic</option>
            {topics.map((k, i) => (
              <option key={i} value={i}>
                Existing: {k.topic}
              </option>
            ))}
          </select>
        </label>
        {target === "new" ? (
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic name, e.g. Wi-Fi (optional)" />
        ) : (
          <p className="text-xs text-muted-foreground">The question is added to that topic, and its answer is replaced with the one above.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}>
            <GraduationCap className="h-4 w-4" /> Teach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
